import type {
  AuthNonceResponse,
  AuthSessionResponse,
  HealthResponse,
  HopTierId,
  MaterializationDetail,
  MaterializationRequest,
  MaterializationSummary,
  SearchRequest,
  SearchResponse,
  SourceAccessResponse,
} from "./types";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const json = async <T>(res: Response): Promise<T> => {
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as T;
};

export const search = async (
  tier: HopTierId,
  body: SearchRequest,
  signal?: AbortSignal,
): Promise<SearchResponse> => {
  const res = await fetch(`/v1/search/${tier}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return json<SearchResponse>(res);
};

export const healthz = async (): Promise<HealthResponse> => {
  const res = await fetch("/healthz");
  return json<HealthResponse>(res);
};

export const createAuthNonce = async (
  wallet: string,
): Promise<AuthNonceResponse> => {
  const res = await fetch(`/v1/auth/nonce?wallet=${encodeURIComponent(wallet)}`);
  return json<AuthNonceResponse>(res);
};

export const createAuthSession = async (body: {
  wallet: string;
  nonce: string;
  signature: string;
}): Promise<AuthSessionResponse> => {
  const res = await fetch("/v1/auth/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return json<AuthSessionResponse>(res);
};

export const listMaterializations = async (
  token: string,
): Promise<MaterializationSummary[]> => {
  const res = await fetch("/v1/me/materializations", {
    headers: { authorization: `Bearer ${token}` },
  });
  return json<MaterializationSummary[]>(res);
};

export const getMaterialization = async (
  token: string,
  id: string,
): Promise<MaterializationDetail> => {
  const res = await fetch(`/v1/me/materializations/${id}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  return json<MaterializationDetail>(res);
};

export const saveMaterialization = async (
  token: string,
  body: MaterializationRequest,
): Promise<MaterializationDetail> => {
  const res = await fetch("/v1/me/materializations", {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return json<MaterializationDetail>(res);
};

export const getSourceAccess = async (
  url: string,
  signal?: AbortSignal,
): Promise<SourceAccessResponse> => {
  const res = await fetch(`/v1/source-access?url=${encodeURIComponent(url)}`, {
    signal,
  });
  return json<SourceAccessResponse>(res);
};
